import { useState, useRef, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { UploadSimple, CheckCircle, User, Briefcase, Code, GraduationCap, MapPin, Link, GithubLogo, LinkedinLogo, Phone, Envelope, Rocket, FilePdf, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const PARSE_STEPS = [
  { text: "Extracting text from PDF...", duration: 2000 },
  { text: "Reading your experience...", duration: 3000 },
  { text: "Identifying skills & expertise...", duration: 3000 },
  { text: "Mapping career timeline...", duration: 2500 },
  { text: "Extracting certifications...", duration: 2000 },
  { text: "Analyzing projects & achievements...", duration: 2500 },
  { text: "Building your profile...", duration: 3000 },
];

const FUN_FACTS = [
  "The average recruiter spends 7.4 seconds on a resume. AI does better.",
  "Your skills are being mapped to 50+ industry taxonomies.",
  "We extract links, metrics, and achievements — not just keywords.",
  "Every bullet point is analyzed for impact and measurability.",
];

function ParsingOverlay() {
  const [stepIdx, setStepIdx] = useState(0);
  const [factIdx, setFactIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const totalDuration = PARSE_STEPS.reduce((s, step) => s + step.duration, 0);
    let elapsed = 0;
    const timers = PARSE_STEPS.map((step, i) => {
      const t = setTimeout(() => setStepIdx(i), elapsed);
      elapsed += step.duration;
      return t;
    });
    const progInterval = setInterval(() => {
      setProgress(p => Math.min(p + (100 / (totalDuration / 200)), 95));
    }, 200);
    const factInterval = setInterval(() => {
      setFactIdx(f => (f + 1) % FUN_FACTS.length);
    }, 4500);
    return () => { timers.forEach(clearTimeout); clearInterval(progInterval); clearInterval(factInterval); };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-[#09090b]/95 backdrop-blur-sm flex items-center justify-center">
      <div className="max-w-md w-full mx-4 text-center">
        <CircleNotch size={48} className="text-amber-500 mx-auto mb-6 animate-spin" weight="bold" />
        <h2 className="font-['Outfit'] text-xl font-semibold text-white mb-2">Parsing your resume</h2>
        <p className="text-sm text-amber-400 font-mono mb-6 h-5 transition-all duration-300">
          {PARSE_STEPS[stepIdx]?.text}
        </p>
        <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden mb-8">
          <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }} />
        </div>
        <div className="flex flex-col gap-2 mb-6">
          {PARSE_STEPS.map((step, i) => (
            <div key={i} className={`flex items-center gap-2 text-xs transition-all duration-300 ${
              i < stepIdx ? 'text-emerald-500' : i === stepIdx ? 'text-amber-400' : 'text-zinc-700'
            }`}>
              {i < stepIdx ? (
                <CheckCircle size={14} weight="fill" className="shrink-0" />
              ) : i === stepIdx ? (
                <CircleNotch size={14} className="shrink-0 animate-spin" />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border border-zinc-700 shrink-0" />
              )}
              <span>{step.text.replace('...', '')}</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-zinc-600 italic transition-all duration-500">
          {FUN_FACTS[factIdx]}
        </p>
      </div>
    </div>
  );
}

export default function ResumePanel() {
  const { profile, loading, uploadResume, activePersona } = useApp();
  const [dragActive, setDragActive] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(false);
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please upload a PDF file");
      return;
    }
    setParsing(true);
    try {
      await uploadResume(file);
      toast.success(activePersona ? `Resume loaded for "${activePersona.name}"` : "Resume parsed successfully");
    } catch (e) {
      toast.error("Failed to parse resume");
    } finally {
      setParsing(false);
    }
  };

  const handleViewPdf = async () => {
    setViewingPdf(true);
    try {
      const token = localStorage.getItem('sankhya_token');
      const fetchUrl = activePersona ? `${API}/resume/view?persona_id=${activePersona.id}` : `${API}/resume/view`;
      const res = await fetch(fetchUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Could not load PDF");
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch {
      toast.error("Failed to open PDF");
    } finally {
      setViewingPdf(false);
    }
  };

  if (parsing) return <ParsingOverlay />;

  return (
    <div className="p-6 lg:p-8 max-w-[900px]" data-testid="resume-panel">
      <div className="mb-8 animate-in">
        <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white">Resume</h1>
        <p className="text-sm text-zinc-500 mt-2">
          {activePersona
            ? <>Uploading for persona: <span className="text-amber-500 font-medium">{activePersona.name}</span></>
            : "Upload your resume for AI-powered parsing and matching"}
        </p>
      </div>

      <div
        data-testid="resume-upload-zone"
        className={`jp-upload-zone animate-in delay-1 ${dragActive ? "active" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => { e.preventDefault(); setDragActive(false); handleUpload(e.dataTransfer.files[0]); }}
        onClick={() => fileRef.current?.click()}
      >
        <input ref={fileRef} type="file" accept=".pdf" className="hidden"
          onChange={(e) => handleUpload(e.target.files[0])} data-testid="resume-file-input" />
        <UploadSimple size={36} className="mx-auto mb-3 text-zinc-600" weight="duotone" />
        <div className="text-sm text-zinc-400">{profile ? "Drop new PDF to replace" : "Drop PDF here or click to upload"}</div>
        <div className="text-[11px] text-zinc-600 mt-2">PDF format supported</div>
      </div>

      {profile && (
        <div className="jp-card mt-6 animate-in delay-2" data-testid="resume-profile">
          {/* Header */}
          <div className="jp-card-header">
            <span className="jp-label">PARSED PROFILE</span>
            <div className="flex items-center gap-3">
              <CheckCircle size={16} className="text-emerald-500" weight="fill" />
              <span className="text-xs text-emerald-500 font-medium">Parsed</span>
              {profile.resume_r2_key ? (
                <button
                  onClick={handleViewPdf}
                  disabled={viewingPdf}
                  className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50 ml-2"
                >
                  <FilePdf size={14} weight="fill" />
                  {viewingPdf ? "Opening…" : "View PDF"}
                </button>
              ) : (
                <span className="text-[10px] text-zinc-600 ml-2">Re-upload to enable PDF view</span>
              )}
            </div>
          </div>

          {/* Name + Headline */}
          <div className="p-5 border-b border-white/5">
            <div className="flex items-start gap-3">
              <User size={20} className="text-amber-500 mt-0.5 shrink-0" weight="duotone" />
              <div>
                <div className="text-base text-white font-semibold">{profile.name || "N/A"}</div>
                {profile.headline && (
                  <div className="text-xs text-amber-400/80 mt-0.5">{profile.headline}</div>
                )}
              </div>
            </div>
          </div>

          {/* Stats bar — only rendered when the resume has metric callouts */}
          {profile.stats && Object.keys(profile.stats).length > 0 && (
            <div className="p-4 border-b border-white/5 bg-white/[0.02]">
              <div className="flex flex-wrap gap-4">
                {Object.entries(profile.stats).map(([label, value], i) => (
                  <div key={i} className="text-center">
                    <div className="text-sm font-bold text-amber-400 font-mono">{value}</div>
                    <div className="text-[10px] text-zinc-600 uppercase tracking-wide">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact + Meta */}
          <div className="grid grid-cols-2 border-b border-white/5">
            <div className="p-5 border-r border-white/5 space-y-2">
              {profile.email && (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Envelope size={12} className="text-zinc-500 shrink-0" />
                  <span className="font-mono truncate">{profile.email}</span>
                </div>
              )}
              {profile.phone && (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Phone size={12} className="text-zinc-500 shrink-0" />
                  <span className="font-mono">{profile.phone}</span>
                </div>
              )}
              {profile.location && (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <MapPin size={12} className="text-zinc-500 shrink-0" />
                  <span>{profile.location}</span>
                </div>
              )}
            </div>
            <div className="p-5 space-y-2">
              <div className="flex items-center gap-2 text-xs text-zinc-400">
                <Briefcase size={12} className="text-zinc-500 shrink-0" />
                <span className="capitalize">{profile.seniority || "N/A"} · {profile.total_years_experience || 0} yrs</span>
              </div>
              {profile.website && (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Link size={12} className="text-zinc-500 shrink-0" />
                  <a href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-amber-400 hover:text-amber-300 truncate transition-colors"
                    onClick={(e) => e.stopPropagation()}>
                    {profile.website.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              )}
              {(profile.linkedin_url || profile.github_url) && (
                <div className="flex items-center gap-3">
                  {profile.linkedin_url && (
                    <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      onClick={(e) => e.stopPropagation()}>
                      <LinkedinLogo size={13} weight="fill" />
                      LinkedIn
                    </a>
                  )}
                  {profile.github_url && (
                    <a href={profile.github_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-zinc-300 hover:text-white transition-colors"
                      onClick={(e) => e.stopPropagation()}>
                      <GithubLogo size={13} weight="fill" />
                      GitHub
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Skills */}
          <div className="p-5 border-b border-white/5">
            <div className="jp-label mb-3 flex items-center gap-2"><Code size={14} weight="duotone" /> SKILLS</div>
            <div className="flex flex-wrap gap-1.5">
              {(profile.skills || []).map((skill, i) => (
                <span key={i} className="jp-badge jp-badge-amber">{skill}</span>
              ))}
            </div>
          </div>

          {/* Preferred Roles */}
          {profile.preferred_roles?.length > 0 && (
            <div className="p-5 border-b border-white/5">
              <div className="jp-label mb-3">PREFERRED ROLES</div>
              <div className="flex flex-wrap gap-1.5">
                {profile.preferred_roles.map((role, i) => (
                  <span key={i} className="jp-badge jp-badge-purple">{role}</span>
                ))}
              </div>
            </div>
          )}

          {/* Experience */}
          {profile.experience?.length > 0 && (
            <div className="p-5 border-b border-white/5">
              <div className="jp-label mb-3 flex items-center gap-2"><Briefcase size={14} weight="duotone" /> EXPERIENCE</div>
              {profile.experience.map((exp, i) => (
                <div key={i} className="mb-5 last:mb-0 pl-4 border-l-2 border-amber-500/20">
                  <div className="text-sm text-white font-medium">{exp.title || exp.role || "Role"}</div>
                  <div className="text-xs text-amber-500">{exp.company || "Company"}</div>
                  <div className="text-[11px] text-zinc-600 mb-1.5">{exp.duration || exp.period || ""}</div>
                  {exp.tech?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {exp.tech.map((t, j) => (
                        <span key={j} className="jp-badge jp-badge-amber text-[10px] px-1.5 py-0.5">{t}</span>
                      ))}
                    </div>
                  )}
                  {exp.description && (
                    <div className="text-xs text-zinc-400 whitespace-pre-line leading-relaxed">{exp.description}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Projects */}
          {profile.projects?.length > 0 && (
            <div className="p-5 border-b border-white/5">
              <div className="jp-label mb-3 flex items-center gap-2"><Rocket size={14} weight="duotone" /> PROJECTS</div>
              {profile.projects.map((proj, i) => (
                <div key={i} className="mb-3 last:mb-0 pl-4 border-l-2 border-purple-500/20">
                  <div className="text-sm text-white font-medium">{proj.name || "Project"}</div>
                  {proj.description && (
                    <div className="text-xs text-zinc-400 mt-0.5">{proj.description}</div>
                  )}
                  {proj.tech?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {proj.tech.map((t, j) => (
                        <span key={j} className="jp-badge jp-badge-amber text-[10px] px-1.5 py-0.5">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Education */}
          {profile.education?.length > 0 && (
            <div className="p-5 border-b border-white/5">
              <div className="jp-label mb-3 flex items-center gap-2"><GraduationCap size={14} weight="duotone" /> EDUCATION</div>
              {profile.education.map((edu, i) => (
                <div key={i} className="mb-2 last:mb-0 pl-4 border-l-2 border-amber-500/20">
                  <div className="text-sm text-white">{edu.degree || "Degree"} {edu.field ? `— ${edu.field}` : ""}</div>
                  <div className="text-xs text-zinc-500">{edu.institution || "Institution"}</div>
                  <div className="text-[11px] text-zinc-600">{edu.year || ""}</div>
                </div>
              ))}
            </div>
          )}

          {/* Certifications */}
          {profile.certifications?.length > 0 && (
            <div className="p-5">
              <div className="jp-label mb-3">CERTIFICATIONS</div>
              <div className="flex flex-wrap gap-1.5">
                {profile.certifications.map((cert, i) => (
                  <span key={i} className="jp-badge jp-badge-purple">{cert}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
